import React, { useState, useMemo } from 'react';
import { FileText, Plus, Trash2, Edit2, ExternalLink, Video, FileDown, Save, X, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { adminApi, API_ENDPOINTS } from './api';
import { ImageUploader } from './ImageUploader';

const BlogForm = ({ item, onSave, onCancel }) => {
  const [f, setF] = useState({
    title: item?.title || '', slug: item?.slug || '', excerpt: item?.excerpt || '', content: item?.content || '',
    image: item?.image || '', author: item?.author || 'Equipe HPD', category: item?.category || '',
    tags: item?.tags?.join(', ') || '', published: item?.published ?? true,
    external_url: item?.external_url || '', video_url: item?.video_url || '', pdf_url: item?.pdf_url || '',
  });
  const up = (k, v) => setF(p => ({ ...p, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    const d = { ...f, slug: f.slug || f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'), tags: f.tags ? f.tags.split(',').map(t => t.trim()) : [] };
    ['external_url', 'video_url', 'pdf_url'].forEach(k => { if (!d[k]) delete d[k]; });
    onSave(d);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-bold text-gray-900">{item ? "Modifier l'article" : "Nouvel article"}</h3>
        <button onClick={onCancel} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-gray-500">Titre *</Label><Input value={f.title} onChange={e => up('title', e.target.value)} required className="h-9" data-testid="blog-form-title" /></div>
          <div><Label className="text-xs text-gray-500">Categorie *</Label><Input value={f.category} onChange={e => up('category', e.target.value)} required className="h-9" /></div>
        </div>
        <div><Label className="text-xs text-gray-500">Resume *</Label><Textarea value={f.excerpt} onChange={e => up('excerpt', e.target.value)} rows={2} required /></div>
        <div><Label className="text-xs text-gray-500">Contenu *</Label><Textarea value={f.content} onChange={e => up('content', e.target.value)} rows={5} required /></div>
        <ImageUploader value={f.image} onChange={v => up('image', v)} label="Image de l'article" />
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-gray-500">Auteur</Label><Input value={f.author} onChange={e => up('author', e.target.value)} className="h-9" /></div>
          <div><Label className="text-xs text-gray-500">Tags (virgule)</Label><Input value={f.tags} onChange={e => up('tags', e.target.value)} className="h-9" /></div>
        </div>
        <details className="bg-gray-50 rounded-xl p-3">
          <summary className="text-xs font-medium text-gray-500 cursor-pointer">Medias optionnels (lien, video, PDF)</summary>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div><Label className="text-[10px] text-gray-400 flex items-center gap-1"><ExternalLink className="w-3 h-3" />Lien externe</Label><Input value={f.external_url} onChange={e => up('external_url', e.target.value)} placeholder="https://..." className="h-8 text-xs" /></div>
            <div><Label className="text-[10px] text-gray-400 flex items-center gap-1"><Video className="w-3 h-3" />Video</Label><Input value={f.video_url} onChange={e => up('video_url', e.target.value)} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px] text-gray-400 flex items-center gap-1"><FileDown className="w-3 h-3" />PDF</Label><Input value={f.pdf_url} onChange={e => up('pdf_url', e.target.value)} className="h-8 text-xs" /></div>
          </div>
        </details>
        <div className="flex gap-2 pt-2">
          <Button type="submit" className="bg-gray-900 hover:bg-gray-800 h-9 rounded-lg text-sm" data-testid="blog-form-save"><Save className="w-4 h-4 mr-1.5" />Sauvegarder</Button>
          <Button type="button" variant="outline" onClick={onCancel} className="h-9 rounded-lg text-sm">Annuler</Button>
        </div>
      </form>
    </div>
  );
};

export const BlogTab = ({ blog, onReload, onReloadStats }) => {
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(false);
  const [search, setSearch] = useState('');

  const reset = () => { setEdit(null); setForm(false); };
  const open = (item = null) => { setEdit(item); setForm(true); };

  const filteredBlog = useMemo(
    () => blog.filter(p => !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.category.toLowerCase().includes(search.toLowerCase())),
    [blog, search]
  );

  const saveBlog = async (d) => {
    try {
      if (edit) await adminApi.put(API_ENDPOINTS.adminBlogSlug(edit.slug), d);
      else await adminApi.post(API_ENDPOINTS.adminBlog, d);
      toast.success(edit ? 'Mis a jour' : 'Cree');
      reset();
      onReload();
      onReloadStats();
    } catch (err) {
      console.error('Save blog failed:', err);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const delBlog = async (slug) => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      await adminApi.del(API_ENDPOINTS.adminBlogSlug(slug));
      toast.success('Supprime');
      onReload();
      onReloadStats();
    } catch (err) {
      console.error('Delete blog failed:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Actualites</h1>
          <p className="text-sm text-gray-500 mt-0.5">{blog.length} articles</p>
        </div>
        <Button onClick={() => open()} className="bg-gray-900 hover:bg-gray-800 text-sm h-9 rounded-lg" data-testid="add-blog-btn">
          <Plus className="w-4 h-4 mr-1.5" />Nouvel article
        </Button>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un article..." className="pl-10 h-10 bg-white border-gray-200 rounded-xl" data-testid="blog-search-input" />
      </div>
      {form && <BlogForm item={edit} onSave={saveBlog} onCancel={reset} />}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Article</th>
              <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Categorie</th>
              <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Auteur</th>
              <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Type</th>
              <th className="p-4 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {filteredBlog.map(p => (
              <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="w-12 h-9 object-cover rounded-lg flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate max-w-md">{p.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{p.views || 0} vues</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 hidden md:table-cell"><span className="text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{p.category}</span></td>
                <td className="p-4 text-sm text-gray-500 hidden lg:table-cell">{p.author}</td>
                <td className="p-4 hidden lg:table-cell">
                  <div className="flex gap-1.5">
                    {p.external_url && <span className="w-6 h-6 bg-blue-50 rounded-md flex items-center justify-center" title="Lien externe"><ExternalLink className="w-3 h-3 text-blue-500" /></span>}
                    {p.video_url && <span className="w-6 h-6 bg-purple-50 rounded-md flex items-center justify-center" title="Video"><Video className="w-3 h-3 text-purple-500" /></span>}
                    {p.pdf_url && <span className="w-6 h-6 bg-red-50 rounded-md flex items-center justify-center" title="PDF"><FileDown className="w-3 h-3 text-red-500" /></span>}
                    {!p.external_url && !p.video_url && !p.pdf_url && <span className="w-6 h-6 bg-gray-50 rounded-md flex items-center justify-center"><FileText className="w-3 h-3 text-gray-400" /></span>}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => open(p)} className="p-1.5 rounded-lg hover:bg-blue-50" data-testid={`edit-blog-${p.slug}`}><Edit2 className="w-3.5 h-3.5 text-blue-500" /></button>
                    <button onClick={() => delBlog(p.slug)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!filteredBlog.length && <p className="text-gray-400 text-center py-8 text-sm">Aucun article trouve</p>}
      </div>
    </div>
  );
};
